import type { ProductHit } from '@libs/algolia';
import { globalFewLeftThreshold } from '@libs/common-next';
import { StockStatusEnum } from '@petsmart-ui/sparky';

type StockStatusProps = {
    minimumCaseQty?: number;
    stockQuantity?: number;
    testFewLeftThreshold?: number; // Varible for tests. Global constant
} & Pick<ProductHit, 'customPharmacyOOS'>;

/**
 * Checks whether the provided custom pharmacy status indicates an out-of-stock condition.
 * FALSE = is IN of stock
 * TRUE = is OUT of stock
 */
const isCustomPharmacyOOS = (customPharmacyOOS: string | boolean | null | undefined) => {
    if (!customPharmacyOOS) {
        return false;
    }

    if (typeof customPharmacyOOS === 'boolean') {
        return customPharmacyOOS;
    }

    return customPharmacyOOS.toLowerCase() === 'true';
};

const defaultToZero = (value: number | null | undefined) => value ?? 0;

export type StockStatusType = 'InStock' | 'FewLeftInStock' | 'OutOfStock' | 'NotAvailableInYourArea' | 'Pending';

const noInventory = (stockQuantity?: number): boolean => defaultToZero(stockQuantity) <= 0;
const validateMinimumInStock = (minimumCaseQty?: number, stockQuantity?: number) =>
    defaultToZero(minimumCaseQty) <= defaultToZero(stockQuantity);

const validateStockStatus = (props: StockStatusProps): StockStatusEnum => {
    const { stockQuantity, minimumCaseQty, testFewLeftThreshold, customPharmacyOOS } = props;

    const isOutOfStock =
        isCustomPharmacyOOS(customPharmacyOOS) ||
        noInventory(stockQuantity) ||
        !validateMinimumInStock(minimumCaseQty, stockQuantity);

    if (isOutOfStock) {
        return StockStatusEnum.OutOfStock;
    }

    const fewLeftThreshold = defaultToZero(
        testFewLeftThreshold === undefined || testFewLeftThreshold === null
            ? globalFewLeftThreshold
            : testFewLeftThreshold
    );
    if (fewLeftThreshold === 0) {
        return StockStatusEnum.InStock;
    }

    const stockBuffer = defaultToZero(stockQuantity) - defaultToZero(minimumCaseQty);
    return stockBuffer <= fewLeftThreshold ? StockStatusEnum.FewLeftInStock : StockStatusEnum.InStock;
};

const validateStockType = (
    { stockQuantity, minimumCaseQty, testFewLeftThreshold, customPharmacyOOS }: StockStatusProps,
    isPageLoading?: boolean
): StockStatusType => {
    if (isPageLoading) {
        return 'Pending';
    }

    const isOutOfStock =
        isCustomPharmacyOOS(customPharmacyOOS) ||
        noInventory(stockQuantity) ||
        !validateMinimumInStock(minimumCaseQty, stockQuantity);

    if (isOutOfStock) {
        return 'OutOfStock';
    }

    const fewLeftThreshold = defaultToZero(
        testFewLeftThreshold === undefined || testFewLeftThreshold === null
            ? globalFewLeftThreshold
            : testFewLeftThreshold
    );

    if (fewLeftThreshold === 0) {
        return 'InStock';
    }

    const stockBuffer = defaultToZero(stockQuantity) - defaultToZero(minimumCaseQty);
    return stockBuffer <= fewLeftThreshold ? 'FewLeftInStock' : 'InStock';
};

const hasStock = (stockStatus: StockStatusEnum) =>
    [StockStatusEnum.InStock, StockStatusEnum.FewLeftInStock].includes(stockStatus);

const stockTypes = ['InStock', 'FewLeftInStock'];

const hasStockType = (stockStatus: StockStatusType) => stockTypes.includes(stockStatus);

export { validateStockStatus, hasStock, validateStockType, hasStockType, isCustomPharmacyOOS };
